import {
  Button,
  ButtonGroup,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Option,
  showNotification,
  Tooltip,
} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';

import {useRef, useState} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {
  BaseSupplierOrderVariantSupplier,
  GetSupplierOrderVariantsResponse,
  PostSupplierOrderRequest,
  useBulkPostSupplierOrderMutation,
  useGetSupplierOrderVariantsQuery,
  useLazyGetSupplierOrderTypeQuery,
  useLazyGetSupplierOrderVariantSuppliersQuery,
  usePostSupplierOrderMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {handleApiError, logError, useGetSupplierCustomersQuery} from '@dms/shared';
import {
  getOptionsFromSupplierAccounts,
  getOptionsFromSupplierOrderTypes,
  getOptionsFromSupplierOrderVariants,
  getOptionsFromSuppliersByOrderVariant,
} from '@dms/warehouse/shared';

import {composePath, Nullish, suffixTestId, TestIdProps, useNavigate} from 'shared/web';

import {
  createSupplierOrderFormSchema,
  CreateSupplierOrderFormType,
} from '../types/createSupplierOrderFormSchema';

type PrevValuesType = {
  orderVariantId?: string;
  supplierId?: string;
};

type FetchSupplierOrderType = {
  supplierId: string;
  supplierOrderVariantId: string;
};

export interface CreateSupplierOrderFormProps extends TestIdProps {
  itemId?: string[];
  onClose: () => void;
  warehouseId?: string;
}

export function CreateSupplierOrderForm(props: CreateSupplierOrderFormProps) {
  const navigate = useNavigate();
  const prevValues = useRef<PrevValuesType>({});

  const [supplierOrderVariantSuppliers, setSupplierOrderVariantSuppliers] = useState<
    BaseSupplierOrderVariantSupplier[] | Nullish
  >(null);

  const [orderVariant, setOrderVariant] = useState<GetSupplierOrderVariantsResponse | Nullish>(
    null
  );
  const [orderTypeOptions, setOrderTypeOptions] = useState<Option[]>([]);
  const [supplierAccountOptions, setSupplierAccountOptions] = useState<Option[]>([]);

  const [createSupplierOrderWithItems, {isLoading: isCreatingSupplierOrderWithItems}] =
    useBulkPostSupplierOrderMutation();
  const [createSupplierOrder, {isLoading: isCreatingSupplierOrder}] =
    usePostSupplierOrderMutation();
  const [getSupplierOrderVariantSuppliers, {isLoading: isSupplierOrderVariantSuppliersLoading}] =
    useLazyGetSupplierOrderVariantSuppliersQuery();
  const [getSupplierOrderType, {isLoading: isSupplierOrderTypeLoading}] =
    useLazyGetSupplierOrderTypeQuery();

  const {
    data: supplierOrderVariants,
    isLoading: areSupplierOrderVariantsLoading,
    isError: hasSupplierOrderVariantsLoading,
  } = useGetSupplierOrderVariantsQuery({
    warehouseId: props.warehouseId,
  });

  const {
    data: suppliers,
    isLoading: areSuppliersLoading,
    isError: hasSuppliersError,
  } = useGetSupplierCustomersQuery();

  const isCreateLoading = isCreatingSupplierOrderWithItems || isCreatingSupplierOrder;

  const isLoading = areSupplierOrderVariantsLoading || areSuppliersLoading;

  const isError = hasSupplierOrderVariantsLoading || hasSuppliersError;

  const isOrderTypeRequired = orderVariant?.isOrderUseIntegration
    ? isNotNilOrEmpty(orderTypeOptions)
    : false;

  const isSupplierAccountRequired = isNotNilOrEmpty(supplierAccountOptions);

  const createSupplierOrderByFormData = (values: CreateSupplierOrderFormType) => {
    const hasItemId = isNotNil(props.itemId);

    if (hasItemId) {
      return createSupplierOrderWithItems({
        requestItemId: props.itemId ?? [],
        orderVariantId: values.orderVariantId ?? '',
        supplierId: values.supplierId ?? '',
        orderType: values.orderType,
        supplierAccountCode: values.supplierAccountCode,
      });
    }

    const request: PostSupplierOrderRequest = {
      orderVariantId: values.orderVariantId ?? '',
      supplierId: values.supplierId ?? '',
      orderType: values.orderType,
      supplierAccountCode: values.supplierAccountCode,
    };

    return createSupplierOrder(request);
  };

  const handleSubmit: FormSubmitHandler<CreateSupplierOrderFormType> = async (values) => {
    await createSupplierOrderByFormData(values)
      .unwrap()
      .then((data) => {
        navigate(
          composePath(warehouseRoutes.supplierOrderDetailOverview, {
            params: {
              id: data.orderId,
            },
          })
        );
      })
      .then(() => {
        showNotification.success(i18n.t('page.warehouse.notification.supplierOrderCreated'));
      })
      .catch(handleApiError);
  };

  const getDefaultSupplierId = (suppliers: BaseSupplierOrderVariantSupplier[] | Nullish) =>
    suppliers?.find((supplier) => supplier.isDefault)?.supplierId;

  return (
    <DataStatus isLoading={isLoading} isError={isError} data-testid={suffixTestId('status', props)}>
      <Form<CreateSupplierOrderFormType>
        experimentalZodSchema={createSupplierOrderFormSchema(
          isOrderTypeRequired,
          isSupplierAccountRequired
        )}
        onSubmit={handleSubmit}
      >
        {(control, formApi) => {
          const orderVariantId = formApi.watch('orderVariantId');
          const supplierId = formApi.watch('supplierId');

          const fetchSupplierOrderType = (args: FetchSupplierOrderType) => {
            getSupplierOrderType({
              supplierId: args.supplierId,
              supplierOrderVariantId: args.supplierOrderVariantId,
            })
              .unwrap()
              .then((response) => {
                const supplierAccounts = getOptionsFromSupplierAccounts(response.supplierAccount);

                setOrderTypeOptions(getOptionsFromSupplierOrderTypes(response.orderType));
                setSupplierAccountOptions(supplierAccounts);

                // If the supplier has exactly one account, select it automatically.
                // Otherwise clear the selection so the user picks one (or none is available).
                // Do not validate here: the form validates on submit, and eagerly setting an
                // error on a still-empty required field cannot be cleared until the next submit.
                formApi.setValue(
                  'supplierAccountCode',
                  supplierAccounts.length === 1 ? supplierAccounts[0].value : undefined,
                  {shouldValidate: false}
                );
              })
              .catch(handleApiError);
          };

          const handleOrderVariantChange = (
            newOrderVariantId: string | string[] | number | null
          ) => {
            if (prevValues.current?.orderVariantId === newOrderVariantId) {
              return;
            }

            const newOrderVariant = supplierOrderVariants?.find(
              (supplierOrderVariant) => supplierOrderVariant.orderVariantId === newOrderVariantId
            );

            setOrderVariant(newOrderVariant);

            getSupplierOrderVariantSuppliers({orderVariantId: newOrderVariantId as string})
              .unwrap()
              .then((suppliers) => {
                setSupplierOrderVariantSuppliers(suppliers);
                const defaultSupplierId = getDefaultSupplierId(suppliers);

                if (isNil(defaultSupplierId)) {
                  return logError('defaultSupplierId is not defined');
                }

                formApi.setValue('supplierId', defaultSupplierId, {shouldValidate: false});
                formApi.setValue('orderType', undefined, {shouldValidate: false});

                prevValues.current = {
                  orderVariantId: newOrderVariantId as string,
                  supplierId: defaultSupplierId,
                };

                if (isString(newOrderVariantId) && isString(defaultSupplierId)) {
                  fetchSupplierOrderType({
                    supplierId: defaultSupplierId,
                    supplierOrderVariantId: newOrderVariantId,
                  });
                }
              })
              .catch(handleApiError);
          };

          const handleSupplierChange = (newSupplierId: string | string[] | number | null) => {
            if (prevValues.current?.supplierId === newSupplierId) {
              return;
            }

            prevValues.current = {
              supplierId: newSupplierId as string,
              orderVariantId: prevValues.current.orderVariantId,
            };
            formApi.setValue('orderType', undefined, {shouldValidate: false});
            const orderVariantId = formApi.getValues('orderVariantId');

            if (isString(orderVariantId) && isString(newSupplierId)) {
              fetchSupplierOrderType({
                supplierId: newSupplierId,
                supplierOrderVariantId: orderVariantId,
              });
            }
          };

          return (
            <VStack spacing={4}>
              <Grid columns={2}>
                <FormField
                  control={control}
                  onChange={handleOrderVariantChange}
                  type="choice"
                  name="orderVariantId"
                  isRequired
                  label={i18n.t('entity.warehouse.labels.supplierOrderVariant')}
                  options={getOptionsFromSupplierOrderVariants(supplierOrderVariants)}
                  data-testid={suffixTestId('inputs.orderVariantId', props)}
                  isNotClearable
                />
                <Tooltip
                  isDisabled={isNotNil(orderVariantId)}
                  label={i18n.t('entity.warehouse.labels.firstlySelectSupplierOrderVariant')}
                >
                  <FormField
                    control={control}
                    onChange={handleSupplierChange}
                    type="choice"
                    name="supplierId"
                    label={i18n.t('entity.warehouse.labels.supplier')}
                    isRequired
                    isDisabled={isNil(orderVariantId)}
                    isLoading={isSupplierOrderVariantSuppliersLoading}
                    options={getOptionsFromSuppliersByOrderVariant(
                      suppliers,
                      supplierOrderVariantSuppliers
                    )}
                    data-testid={suffixTestId('inputs.supplierId', props)}
                    isNotClearable
                  />
                </Tooltip>
                <Tooltip
                  isDisabled={isNotNil(supplierId)}
                  label={i18n.t(
                    'entity.warehouse.labels.firstlySelectSupplierOrderVariantAndSupplier'
                  )}
                >
                  <FormField
                    control={control}
                    type="choice"
                    name="orderType"
                    label={i18n.t('entity.warehouse.labels.supplierOrderType')}
                    isRequired={isOrderTypeRequired}
                    isDisabled={isNil(supplierId)}
                    options={orderTypeOptions}
                    data-testid={suffixTestId('inputs.orderType', props)}
                    isNotClearable
                    isLoading={isSupplierOrderTypeLoading}
                  />
                </Tooltip>
                <Tooltip
                  isDisabled={isNotNil(supplierId)}
                  label={i18n.t(
                    'entity.warehouse.labels.firstlySelectSupplierOrderVariantAndSupplier'
                  )}
                >
                  <FormField
                    control={control}
                    type="choice"
                    name="supplierAccountCode"
                    label={i18n.t('entity.warehouse.labels.supplierAccount')}
                    isRequired={isSupplierAccountRequired}
                    isDisabled={isNil(supplierId) || supplierAccountOptions.length <= 1}
                    options={supplierAccountOptions}
                    data-testid={suffixTestId('inputs.supplierAccountCode', props)}
                    isNotClearable
                    isLoading={isSupplierOrderTypeLoading}
                  />
                </Tooltip>
              </Grid>
              <ButtonGroup align="right">
                <Button
                  title={i18n.t('general.actions.discard')}
                  variant="secondary"
                  onClick={props.onClose}
                  data-testid={suffixTestId('actions.discard', props)}
                />
                <FormButton
                  control={control}
                  type="submit"
                  title={i18n.t('general.actions.create')}
                  isLoading={isCreateLoading}
                  data-testid={suffixTestId('actions.create', props)}
                />
              </ButtonGroup>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}
